import React, { useState, useEffect, useRef, useCallback } from 'react';
import Config from '../../config';
import axios from 'axios';
import { Grid, Card, List, ListItem as ListItemBase, ListItemText, Toolbar, Typography, CardContent, Chip, Link as MuiLink } from '@mui/material';
import ExtendedSelectBase from '../../components/select';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CheckIcon from '@mui/icons-material/Check';
import testing from '../../images/testing.svg';
import products from '../../images/products.svg';
import inventory from '../../images/inventory.svg';
import shipping_label from '../../images/shipping_label.svg';
import { Link } from 'react-router-dom';
import ErrorIcon from '@mui/icons-material/Error';
import ConfirmDialog from '../../components/confirm_dialog';
import DownloadButton from '../../components/download_button';
import help_center from '../../images/help_center.svg';
import quickstart from '../../images/quickstart.svg';
import faq from '../../images/faq.svg';
import add_user from '../../images/add_user.svg';
import connect from '../../images/connections.svg';
import Auth from '../../auth';
import SectionToolbar from './section_toolbar';
import ShippingLabelDialog from './shipping_label_dialog';
import { ShippingLabelStatus, SHIPPING_LABEL_TASK_TITLE } from './shippingLabelStatus';

// ExtendedSelect is a JS component; its inferred type only exposes RefAttributes,
// so cast to ComponentType<any> to allow the variant/options/onChange/style props.
const ExtendedSelect = ExtendedSelectBase as React.ComponentType<any>;

// Relax legacy `button` prop typing on ListItem to `any` (behavior unchanged).
const ListItem: any = ListItemBase;

/**
 * Onboarding "task" shape shared by the embedded (supplier-facing) and
 * retailer-facing onboarding task lists. Mirrors `PartnerTasks` in
 * `./partnerTasks.ts` (kept independent here to avoid a circular import,
 * since `partnerTasks.ts` imports `loadPartnerDetails` from this file).
 */
export interface ConnectionTask {
    connectionPrimary?: string | null;
    connectionSecondary?: React.ReactNode | null;
    connectionLink?: string | null;
}

export interface PartnerOnboardingTasks extends ConnectionTask {
    inventoryEnabled?: boolean;
    inventoryPosted?: boolean;
    productPosted?: boolean;
    productEnabled?: boolean;
    productPoc?: boolean;
    testingEnabled?: boolean;
    testingComplete?: boolean;
    connectionConfigured?: boolean;
    integrationType?: string;
}

interface IntegrationForTasks {
    CoId?: number | string;
    IntegrationType?: string;
}

const AssignmentIcon = ({ src }: { src: string }) => {
    return <img style={{height: '2.5em', width: '2.5em', marginRight: '0.5em', filter: 'brightness(0.4)'}} src={src} alt=''/>
}

const AssignmentIndicator = ({ passed, failed, label }: { passed?: boolean; failed?: boolean; label?: string }) => {
    let color = passed ? '#008105' : failed ? '#ee3224' : '#ffa500';
    let text = label || (passed ? 'Complete' : 'Incomplete');
    return <Chip label={text} size='small' style={{backgroundColor: color, color: 'white', fontWeight: 'bold', minWidth: '8em', marginRight: '0.5em', marginLeft: '1em', cursor: 'pointer' }}/>;
}

const ShippingLabelIndicator = ({ status }: { status?: string | null }) => {
    return <AssignmentIndicator passed={status === ShippingLabelStatus.Complete} failed={status === ShippingLabelStatus.Rejected} label={status || 'Incomplete'}/>;
}

const getRetailerShippingLabelDescription = (status?: string | null) => {
    if(status === ShippingLabelStatus.WaitingReview){
        return 'This company has uploaded a shipping label for your review.';
    }
    if(status === ShippingLabelStatus.Rejected){
        return 'This company\'s shipping label was rejected and needs to be re-uploaded.';
    }
    if(status === ShippingLabelStatus.Complete){
        return 'This company\'s shipping label has been approved.';
    }
    return 'This company has not uploaded a shipping label.';
}

const ShowMe = ({ href }: { href: string }) => {
    return <MuiLink className='linkButton' sx={{ cursor: 'pointer' }} onClick={(e: React.MouseEvent) => {e.preventDefault(); window.open(href,'_blank','noopener');}}>Show me how</MuiLink>;
}

const connectionLinks: Record<string, string> = {
    "shopify": "https://help.logicbroker.com/hc/en-us/articles/10220732867220-Shopify-for-Suppliers",
    "squarespace": "https://help.logicbroker.com/hc/en-us/articles/28564532173716-Squarespace",
    "shipstation": "https://help.logicbroker.com/hc/en-us/articles/6432094502420-ShipStation"
}

const getConnectionTask = (integrationType?: string): ConnectionTask => {
    let type = (integrationType || "portal").toLowerCase();
    if(type === "portal"){
        return {connectionPrimary: null, connectionSecondary: null, connectionLink: null};
    }
    if(type === "api"){
        return {connectionPrimary: "Set up API connection", connectionSecondary: <>Set up your API connection and generate your API key. <ShowMe href="https://help.logicbroker.com/hc/en-us/articles/360022068791"/></>, connectionLink: "/profile/api-authentication"}
    }
    if(type === "edi"){
        return {connectionPrimary: "Set up your EDI connection", connectionSecondary: <>Set up your EDI connection and collect your partner's details to configure in your system. <ShowMe href="https://help.logicbroker.com/hc/en-us/articles/20332257867540"/></>, connectionLink: "/settings/edi"}
    }
    let link: React.ReactNode = '';
    if(connectionLinks[type]){
        link = <ShowMe href={connectionLinks[type]}/>;
    }
    return {connectionPrimary: `Set up your ${integrationType} connection`, connectionSecondary: <>Connect your {integrationType} account to Logicbroker. {link}</>, connectionLink: "/connections"}
}

interface PartnerSummary {
    Id: number | string;
    CompanyName: string;
    [key: string]: unknown;
}

interface PartnerDetail extends PartnerOnboardingTasks {
    partner: number | string;
    partnerName: string;
    rejectionMessage?: string | null;
    partnerMessage?: string | null;
    integrationId?: number | string;
    canRequestGoLive?: boolean;
    tasksComplete?: boolean;
    requestedGoLive?: boolean;
    shippingLabelEnabled?: boolean;
    shippingLabelStatus?: string | null;
}

interface EmbeddedOnboardingProps {
    show?: boolean;
    [key: string]: unknown;
}

// Loosely typed: state is built by spreading a dynamically-selected
// PartnerDetail (plus ConnectionTask fields) on top of the base shape below,
// so most fields are only present once a partner has loaded.
interface EmbeddedOnboardingState extends Partial<PartnerDetail> {
    hidden: boolean;
    partners: PartnerSummary[];
    showConfirm: boolean;
    partnerDetails?: PartnerDetail[];
    showError?: boolean;
    error?: string;
    confirmMessage?: string;
    onConfirm?: () => void;
    loading?: boolean;
    showShippingLabel?: boolean;
}

const EmbeddedOnboarding = (props: EmbeddedOnboardingProps) => {
    const [state, setState] = useState<EmbeddedOnboardingState>({hidden: true, partners: [], showConfirm: false});
    // Mirror the class instance's live `this.state`: async handlers (requestGoLive,
    // hideProductionMessage) and the stored `onConfirm` closure read the LATEST
    // values at call time rather than the values captured at render.
    const stateRef = useRef(state);
    stateRef.current = state;

    const loadData = useCallback(async (partnerId?: number | string) => {
        //Check for active onboardings
        let filter = Config.production ? "Status eq 'Live'" : "Status ne 'Live'";
        let onboardings = (await axios.get(Config.oauth + `/api/odata/supplierintegrations?$select=Id,RequesterId,Status,Error,ErrorMessage,StageMessage,ProductionMessage,HideProductionMessage,IntegrationType,EnableShippingLabelStep,RequireAllSteps,ShippingLabelStatus&$filter=${filter}`)).data.value;
        //Filter to only partners with messages and not hidden in production
        if(Config.production){
            onboardings = onboardings.filter((r: any) => !(!r.ProductionMessage) && !r.HideProductionMessage);
        }
        if(onboardings.length === 0){
            setState(prev => ({...prev, hidden: true}));
            return;
        }
        //Filter to only active partners
        let partners: PartnerSummary[] = (await axios.get(Config.api + '/api/v1/Partners')).data.Body.Partners.filter((r: any) => onboardings.find((x: any) => x.RequesterId === r.Id)).sort((a: PartnerSummary, b: PartnerSummary) => a.CompanyName.localeCompare(b.CompanyName));
        if(partners.length === 0){
            return;
        }
        let testingStatus = Config.production ? [] : (await axios.get(Config.api + "/odata/Company/VwTestingProgress?$select=PartnerCoId,TotalTests,PassedTests")).data.value;
        let partnerDetails: PartnerDetail[] = [];
        for(let i = 0; i < partners.length; i++){
            let partner = partners[i].Id;
            let partnerName = partners[i].CompanyName;
            let inventorySummary = (await axios.get(Config.api + `/odata/Company/Functions.InventorySummary?partnerId=${partner}`)).data;
            let inventoryEnabled = !(!inventorySummary.HasValidation);
            let inventoryPosted = !(!inventorySummary.LastUpdate);
            let productPosted = false;
            let productEnabled = false;
            let productPoc = false;
            let testingEnabled = testingStatus.filter((r: any) => r.PartnerCoId === partner && r.TotalTests > 0).length > 0;
            let testingComplete = testingStatus.filter((r: any) => r.PartnerCoId === partner && r.TotalTests === r.PassedTests).length > 0;
            try{
                let spec = (await axios.get(Config.api + `/odata/company/Functions.ProductConfiguration?partnerId=${partner}`)).data;
                productPoc = (spec || {}).CatalogManager;
                let productDisabled = (spec || {}).DisableOnboarding === true;
                if(!productDisabled){
                    productEnabled = true;
                    if(productPoc){
                        let items = (await axios.get(Config.api + `/odata/Company/Functions.SupplierCatalogSearch?$select=id&$top=1&$filter=merchantId eq ${partner}`)).data;
                        productPosted = items.value.length > 0;
                    } else {
                        let feeds = (await axios.get(Config.api + `/odata/Company/Functions.ProductFeedSearch?$select=id&$filter=merchantId eq ${partner} and status eq 'Complete'&$top=1`)).data;
                        productPosted = feeds.value.length > 0;
                    }
                }
            }catch(e){
            }
            let ticket: any = onboardings.find((x: any) => x.RequesterId === partner) || {};
            let integrationType = ticket.IntegrationType || "Portal";
            let connectionConfigured = (await axios.get(Config.api + `/odata/company/Functions.IsSystemConfigured?coid=${Auth.getAccountNumber()}&system=${integrationType}`)).data.value;
            let connectionTask = getConnectionTask(integrationType);
            let partnerMessage = Config.production ? ticket.ProductionMessage : ticket.StageMessage;
            let rejectionMessage = ticket.Error ? ticket.ErrorMessage : null;
            let status = (ticket.Status || '').toLowerCase();
            let integrationId = ticket.Id;
            //let status = 'onboarding'.toLowerCase();
            let requestedGoLive = status === 'go live requested';
            let shippingLabelEnabled = !(!ticket.EnableShippingLabelStep);
            let shippingLabelStatus = ticket.ShippingLabelStatus || null;
            //Connection setup is intentionally excluded from task completion
            let tasksComplete = inventoryPosted && (!productEnabled || productPosted) && (!testingEnabled || testingComplete) && (!shippingLabelEnabled || shippingLabelStatus === ShippingLabelStatus.Complete);
            let canRequestGoLive = (status === 'deployed to stage' || status === 'onboarding') && (!ticket.RequireAllSteps || tasksComplete);
            partnerDetails.push({partner, partnerName, rejectionMessage, partnerMessage, integrationId, inventoryEnabled, inventoryPosted, productPoc, productPosted, productEnabled, testingEnabled, testingComplete, canRequestGoLive, tasksComplete, requestedGoLive, connectionConfigured, shippingLabelEnabled, shippingLabelStatus, ...connectionTask});
        }
        let firstPartner = (partnerId ? partnerDetails.find(x => x.partner === partnerId) : partnerDetails[0]) || partnerDetails[0];
        setState(prev => ({...prev, hidden: false, partners, partnerDetails, ...firstPartner}));
    }, []);

    useEffect(() => {
        if(props.show){
            setState(prev => ({...prev, hidden: false}));
        }
        loadData();
        // Runs once on mount (mirrors componentDidMount); loadData is stable.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onPartnerSwitch = (e: number | string) => {
        if(!e){
            return;
        }
        let details = state.partnerDetails.filter(r => r.partner === e)[0];
        setState(prev => ({...prev, ...details}));
    }

    const requestGoLive = async (confirm?: boolean) => {
        if(confirm){
            try{
                await axios.post(Config.oauth + '/integration/RequestGoLive/' + stateRef.current.integrationId);
                setState(prev => ({...prev, showConfirm: false}));
                await loadData(stateRef.current.partner);
            }catch(e: any){
                let error = 'Unexpected error.';
                if(e.response && e.response.data && e.response.data.error){
                    error = e.response.data.error;
                }
                setState(prev => ({...prev, showConfirm: false, showError: true, error}));
            }
        } else {
            setState(prev => ({...prev, showConfirm: true, confirmMessage: 'Once you request to go live the retailer will review your onboarding tasks and approve or reject your request. Are you sure you want to continue?', onConfirm: () => requestGoLive(true)}));
        }
    }

    const hideProductionMessage = async (confirm?: boolean) => {
        if(confirm){
            try{
                await axios.post(Config.oauth + '/integration/HideProductionMessage/' + stateRef.current.integrationId);
                setState(prev => ({...prev, showConfirm: false}));
                await loadData(stateRef.current.partner);
            }catch(e: any){
                let error = 'Unexpected error.';
                if(e.response && e.response.data && e.response.data.error){
                    error = e.response.data.error;
                }
                setState(prev => ({...prev, showConfirm: false, showError: true, error}));
            }
        } else {
            setState(prev => ({...prev, showConfirm: true, confirmMessage: 'Please make sure you have completed all onboarding tasks in production. Are you sure you want to hide this message?', onConfirm: () => hideProductionMessage(true)}));
        }
    }

    if(state.hidden){
        return '';
    }
    return <>
        <Grid size={{ sm: 8, md: 8, lg: 8 }} style={{display: 'flex'}}>
            <Card style={{flex: 1, display: 'flex', flexDirection: 'column'}}>
            <Toolbar className='lbtoolbar' style={{flex: 0}}>{Config.production ? 'Production Tasks' : 'Onboarding Tasks'}</Toolbar>
                <ExtendedSelect style={{flex: 0, margin: '1em 1em 0 1em'}} variant='outlined' placeholder='Select partner...' onChange={onPartnerSwitch} value={state.partner} options={state.partners.map((i) => {return {label: i.CompanyName, value: i.Id};})}/>
                {state.rejectionMessage ? <div style={{backgroundColor: '#ee3224', color: 'white', marginTop: '1em', padding: '0.5em'}}>
                    <div style={{display: 'flex', alignItems: 'center'}}>
                            <ErrorIcon style={{marginRight: '1rem'}}/><div style={{display: 'inline-block'}}>{state.rejectionMessage}</div>
                    </div>
                </div> : ''}
                <List style={{ padding: 0, flex: 1 }}>
                    {state.shippingLabelEnabled && <ListItem button onClick={() => setState(prev => ({...prev, showShippingLabel: true}))} sx={{ cursor: 'pointer' }}>
                        <AssignmentIcon src={shipping_label}/>
                        <ListItemText style={{ color: 'rgba(0, 0, 0, 0.87)' }} primary={SHIPPING_LABEL_TASK_TITLE} secondary={<>Upload the shipping label you will use with this partner for approval. <ShowMe href="https://support.logicbroker.com/kb/logicbroker/upload-shipping-label-for-approval"/></>} />
                        <ShippingLabelIndicator status={state.shippingLabelStatus}/>
                        <ArrowForwardIosIcon color="action" />
                    </ListItem>}
                    {!(!state.connectionPrimary) && <ListItem button component={Link} to={state.connectionLink}>
                        <AssignmentIcon src={connect}/>
                        <ListItemText style={{ color: 'rgba(0, 0, 0, 0.87)' }} primary={state.connectionPrimary} secondary={state.connectionSecondary}/>
                        <AssignmentIndicator passed={state.connectionConfigured}/>
                        <ArrowForwardIosIcon color="action" />
                    </ListItem>}
                    {state.productEnabled && !state.productPoc && <ListItem button component={Link} to={`/product-feeds?partner=${state.partner}`}>
                        <AssignmentIcon src={products}/>
                        <ListItemText style={{ color: 'rgba(0, 0, 0, 0.87)' }} primary='Upload/send product feed' secondary={<>Review your partner's product feed specifications and upload/send a feed. <ShowMe href="https://help.logicbroker.com/hc/en-us/articles/22031708891284-Upload-or-send-a-product-feed"/></>} />
                        <AssignmentIndicator passed={state.productPosted}/>
                        <ArrowForwardIosIcon color="action" />
                    </ListItem>}
                    {state.productEnabled && state.productPoc && <ListItem button component={Link} to={'/productonboardingcenter/catalog?catalog=mycatalog'}>
                        <AssignmentIcon src={products}/>
                        <ListItemText style={{ color: 'rgba(0, 0, 0, 0.87)' }} primary='Upload/send products' secondary={<>Review your partner's product specifications and upload/send a file. <ShowMe href="https://help.logicbroker.com/hc/en-us/articles/27789943193236-Upload-or-send-a-product-file"/></>} />
                        <AssignmentIndicator passed={state.productPosted}/>
                        <ArrowForwardIosIcon color="action" />
                    </ListItem>}
                    {state.inventoryEnabled && <ListItem button component={Link} to={`/advanced-product-management?q.0=merchantName eq ${state.partnerName}`}>
                        <AssignmentIcon src={inventory}/>
                        <ListItemText style={{ color: 'rgba(0, 0, 0, 0.87)' }} primary='Upload/send inventory' secondary={<>Review your partner's inventory feed specifications and upload/send a feed. <ShowMe href="https://help.logicbroker.com/hc/en-us/articles/22031816856724-Upload-or-send-inventory"/></>} />
                        <AssignmentIndicator passed={state.inventoryPosted}/>
                        <ArrowForwardIosIcon color="action" />
                    </ListItem>}
                    {state.testingEnabled && <ListItem button component={Link} to={`/testing/cases?partner=${state.partner}`}>
                        <AssignmentIcon src={testing}/>
                        <ListItemText style={{ color: 'rgba(0, 0, 0, 0.87)' }} primary='Complete test cases' secondary={<>Process test cases using your preferred connection method to prepare for live orders. <ShowMe href="https://help.logicbroker.com/hc/en-us/articles/22031822728468"/></>} />
                        <AssignmentIndicator passed={state.testingComplete}/>
                        <ArrowForwardIosIcon color="action" />
                    </ListItem>}
                </List>
                {state.partnerMessage && <CardContent style={{textAlign: 'left', background: '#f6f6f6', borderRadius: '4px', margin: '16px 16px 0px'}}>
                        <Typography style={{fontWeight: 'bold'}}>A message from {state.partnerName}</Typography>
                        <div dangerouslySetInnerHTML={{__html: state.partnerMessage}}></div>
                    </CardContent>}
                {!Config.production && <div style={{textAlign: 'right', margin: '1em'}}>
                    <DownloadButton loading={state.loading} onClick={() => requestGoLive()} disabled={!state.canRequestGoLive} color={state.tasksComplete ? 'primary' : 'default'} variant='outlined'>{state.requestedGoLive ? <><CheckIcon style={{verticalAlign: 'middle'}}/> Requested to go live</> : 'Request to go live'}</DownloadButton>
                </div>}
                {Config.production && <div style={{textAlign: 'right', margin: '1em'}}>
                    <DownloadButton loading={state.loading} onClick={() => hideProductionMessage()} variant='outlined'>Dismiss</DownloadButton>
                </div>}
            </Card>
        </Grid>
        <Grid size={{ sm: 4, md: 4, lg: 4 }} style={{display: 'flex'}}>
            <Card style={{flex: 1}}>
            <Toolbar className='lbtoolbar'>Quick Reference</Toolbar>
                <List style={{ padding: 0 }}>
                    <ListItem button component='a' href='https://help.logicbroker.com/hc/en-us/articles/360021857352' target='_blank'>
                        <img style={{height: '2.5em', width: '2.5em', marginRight: '0.5em', filter: 'brightness(0.4)'}} src={add_user} alt=''/>
                        <ListItemText style={{ color: 'rgba(0, 0, 0, 0.87)' }} primary="Add a new user" secondary='Invite your teammates to the Logicbroker portal.' />
                        <ArrowForwardIosIcon color="action" />
                    </ListItem>
                    <ListItem button component='a' href='https://help.logicbroker.com/hc/en-us/articles/4416258816276' target='_blank'>
                        <img style={{height: '2.5em', width: '2.5em', marginRight: '0.5em', filter: 'brightness(0.4)'}} src={faq} alt=''/>
                        <ListItemText style={{ color: 'rgba(0, 0, 0, 0.87)' }} primary="FAQs" secondary='See frequently asked onboarding questions.' />
                        <ArrowForwardIosIcon color="action" />
                    </ListItem>
                    <ListItem button onClick={() => (document.querySelectorAll('#helpCenterMenu a')[0] as HTMLElement).click()} sx={{ cursor: 'pointer' }}>
                        <img style={{height: '2.5em', width: '2.5em', marginRight: '0.5em', filter: 'brightness(0.4)'}} src={help_center} alt=''/>
                        <ListItemText style={{ color: 'rgba(0, 0, 0, 0.87)' }} primary="Knowledge base" secondary='See our platform documentation and browse articles.' />
                        <ArrowForwardIosIcon color="action" />
                    </ListItem>
                    <ListItem button component='a' href='https://help.logicbroker.com/hc/en-us/categories/4404975924884-Quick-Start-Guides' target='_blank'>
                        <img style={{height: '2.5em', width: '2.5em', marginRight: '0.5em', filter: 'brightness(0.4)'}} src={quickstart} alt=''/>
                        <ListItemText style={{ color: 'rgba(0, 0, 0, 0.87)' }} primary="Quick start guides" secondary='Complete common tasks with short and easy downloadable guides.' />
                        <ArrowForwardIosIcon color="action" />
                    </ListItem>
                </List>
            </Card>
        </Grid>
        <ConfirmDialog open={state.showConfirm} onClose={() => setState(prev => ({...prev, showConfirm: false}))} message={state.confirmMessage} onConfirm={state.onConfirm}/>
        <ShippingLabelDialog open={!!state.showShippingLabel} onClose={() => setState(prev => ({...prev, showShippingLabel: false}))} integrationId={state.integrationId} status={state.shippingLabelStatus} onChange={() => loadData(stateRef.current.partner)}/>
    </>;
}

const SupplierOnboarding = (props: {}) => {
    return <Grid container spacing={2}>
        <EmbeddedOnboarding show={true} {...props}/>
    </Grid>;
}

const loadPartnerDetails = async (integration?: IntegrationForTasks | null): Promise<PartnerOnboardingTasks | null> => {
    if(!integration || !integration.CoId){
        return null;
    }
    let partner = integration.CoId;
    let testingStatus = (await axios.get(Config.api + `/odata/Company/VwTestingProgress?$select=PartnerCoId,TotalTests,PassedTests&$filter=PartnerCoId eq ${partner}`)).data.value;
    let inventorySummary: any = {};
    try{
        inventorySummary = (await axios.get(Config.api + `/odata/Company/Functions.InventorySummary?partnerId=${partner}`)).data;
    }catch{
        return null;
    }
    let inventoryEnabled = !(!inventorySummary.HasValidation);
    let inventoryPosted = !(!inventorySummary.LastUpdate);
    let productPosted = false;
    let productEnabled = false;
    let productPoc = false;
    let testingEnabled = testingStatus.filter((r: any) => r.PartnerCoId === partner && r.TotalTests > 0).length > 0;
    let testingComplete = testingStatus.filter((r: any) => r.PartnerCoId === partner && r.TotalTests === r.PassedTests).length > 0;
    let integrationType = integration.IntegrationType || "Portal";
    let connectionConfigured = (await axios.get(Config.api + `/odata/company/Functions.IsSystemConfigured?coid=${partner}&system=${integrationType}`)).data.value;
    let connectionTask = getConnectionTask(integrationType);
    try{
        let spec = (await axios.get(Config.api + `/odata/company/Functions.ProductConfiguration?partnerId=${partner}`)).data;
        productPoc = (spec || {}).CatalogManager;
        let productDisabled = (spec || {}).DisableOnboarding === true;
        if(!productDisabled){
            productEnabled = true;
            if(productPoc){
                let items = (await axios.get(Config.api + `/odata/Company/Functions.SupplierCatalogSearch?$select=id&$top=1&$filter=supplierId eq ${partner}`)).data;
                productPosted = items.value.length > 0;
            } else {
                let feeds = (await axios.get(Config.api + `/odata/Company/Functions.ProductFeedSearch?$select=id&$filter=supplierId eq ${partner} and status eq 'Complete'&$top=1`)).data;
                productPosted = feeds.value.length > 0;
            }
        }
    }catch(e){
    }
    return {inventoryEnabled, inventoryPosted, productPosted, productEnabled, productPoc, testingEnabled, testingComplete, connectionConfigured, integrationType, ...connectionTask};
}

interface RetailerOnboardingProps {
    partner?: number | string | null;
    partnerName?: string;
    tasks?: PartnerOnboardingTasks | null;
    useNewStyles?: boolean;
    shippingLabelEnabled?: boolean;
    shippingLabelStatus?: string | null;
    integrationId?: number | string;
    canApproveShippingLabel?: boolean;
    canRejectShippingLabel?: boolean;
    onShippingLabelChange?: () => void | Promise<void>;
}

const RetailerOnboarding = (props: RetailerOnboardingProps) => {
    let {partner, partnerName, tasks, useNewStyles, shippingLabelEnabled, shippingLabelStatus, integrationId, canApproveShippingLabel, canRejectShippingLabel, onShippingLabelChange} = props;
    const [showShippingLabel, setShowShippingLabel] = useState(false);
    if(!tasks){
        tasks = {};
    }
    if(!partner){
        return <Card style={{flex: 1}}>
    {useNewStyles ? <SectionToolbar>Onboarding Tasks</SectionToolbar> : <Toolbar className='lbtoolbar'>Onboarding Tasks</Toolbar>}
        <CardContent>
            <Typography style={{textAlign: 'left'}}>This company is not yet available in {Config.production ? 'production' : 'stage'}. Once they have been deployed you will be able to see their onboarding tasks here.</Typography>
        </CardContent>
    </Card>;
    }
    return <Card style={{flex: 1}}>
    {useNewStyles ? <SectionToolbar>Onboarding Tasks</SectionToolbar> : <Toolbar className='lbtoolbar'>Onboarding Tasks</Toolbar>}
        <List style={{ padding: 0 }}>
            {shippingLabelEnabled && <ListItem button onClick={() => setShowShippingLabel(true)} sx={{ cursor: 'pointer' }}>
                <AssignmentIcon src={shipping_label}/>
                <ListItemText style={{ color: 'rgba(0, 0, 0, 0.87)' }} primary={SHIPPING_LABEL_TASK_TITLE} secondary={getRetailerShippingLabelDescription(shippingLabelStatus)}/>
                <ShippingLabelIndicator status={shippingLabelStatus}/>
                <ArrowForwardIosIcon color="action" />
            </ListItem>}
            {!(!tasks.connectionPrimary) && <ListItem>
                <AssignmentIcon src={connect}/>
                <ListItemText style={{ color: 'rgba(0, 0, 0, 0.87)' }} primary={`Set up ${tasks.integrationType} connection`} secondary={`This company has${tasks.connectionConfigured ? ' ' : ' not '} configured their ${tasks.integrationType} connection.`}/>
                <AssignmentIndicator passed={tasks.connectionConfigured}/>
                <ArrowForwardIosIcon color="action" style={{visibility: 'hidden'}} />
            </ListItem>}
            {tasks.productEnabled && !tasks.productPoc && <ListItem button component={Link} to={`/product-feeds?partner=${partner}`}>
                <AssignmentIcon src={products}/>
                <ListItemText style={{ color: 'rgba(0, 0, 0, 0.87)' }} primary="Upload product feed" secondary={`This company has${tasks.productPosted ? ' ' : ' not '} uploaded a product feed.${tasks.productPosted ? ' Please review to make sure there are no errors and ensure compliance.' : ''}`}/>
                <AssignmentIndicator passed={tasks.productPosted}/>
                <ArrowForwardIosIcon color="action" />
            </ListItem>}
            {tasks.productEnabled && tasks.productPoc && <ListItem button component={Link} to={`/productonboardingcenter/catalog?q.0=supplierId eq ${partner}&catalog=partnercatalog`}>
                <AssignmentIcon src={products}/>
                <ListItemText style={{ color: 'rgba(0, 0, 0, 0.87)' }} primary="Upload products" secondary={`This company has${tasks.productPosted ? ' ' : ' not '} uploaded products.${tasks.productPosted ? ' Please review to make sure there are no errors and ensure compliance.' : ''}`}/>
                <AssignmentIndicator passed={tasks.productPosted}/>
                <ArrowForwardIosIcon color="action" />
            </ListItem>}
            {tasks.inventoryEnabled && <ListItem button component={Link} to={`/advanced-product-management?q.0=supplierName eq ${partnerName}`}>
                <AssignmentIcon src={inventory}/>
                <ListItemText style={{ color: 'rgba(0, 0, 0, 0.87)' }} primary="Upload inventory" secondary={`This company has${tasks.inventoryPosted ? ' ' : ' not '} uploaded an inventory feed.${tasks.inventoryPosted ? ' Please review to make sure there are no errors and ensure compliance.' : ''}`}/>
                <AssignmentIndicator passed={tasks.inventoryPosted}/>
                <ArrowForwardIosIcon color="action" />
            </ListItem>}
            {!Config.production && tasks.testingEnabled && <ListItem button component={Link} to={`/testing/cases?partner=${partner}`}>
                <AssignmentIcon src={testing}/>
                <ListItemText style={{ color: 'rgba(0, 0, 0, 0.87)' }} primary="Complete test cases" secondary={`This company has${tasks.testingComplete ? ' ' : ' not '} completed the test cases.`}/>
                <AssignmentIndicator passed={tasks.testingComplete}/>
                <ArrowForwardIosIcon color="action" />
            </ListItem>}
        </List>
        <ShippingLabelDialog open={showShippingLabel} onClose={() => setShowShippingLabel(false)} integrationId={integrationId} status={shippingLabelStatus} isRetailer canApprove={canApproveShippingLabel} canReject={canRejectShippingLabel} onChange={onShippingLabelChange}/>
    </Card>;
}

export default SupplierOnboarding;

export { EmbeddedOnboarding, RetailerOnboarding, loadPartnerDetails };
